import React, { useState } from 'react';

// Define the full SQL template with placeholders for dynamic insertion
const SQL_TEMPLATE = `
WITH base_contract_customers_daily AS (
  SELECT *
  FROM \`cloudflare-bi.bi_public.base_contract_customers_daily\` 
  WHERE event_date = <!-- EVENT_DATE_WHERE_CLAUSE -->
)

, base_zones_daily AS (
  SELECT * FROM \`cloudflare-bi.bi_public.base_zones_daily\`
   WHERE event_date = <!-- EVENT_DATE_WHERE_CLAUSE -->
   AND <!-- EXCLUDE_INTERNAL_ZONES_WHERE_CLAUSE -->
)

, metrics_daily AS (
  SELECT * FROM <!-- METRICS_TABLE_PLACEHOLDER -->
   WHERE event_date = <!-- EVENT_DATE_WHERE_CLAUSE -->
)

, all_contracted_customers AS (
  SELECT DISTINCT sf_account_id
  FROM base_contract_customers_daily
  WHERE is_contracted
)

, all_pof AS (
  SELECT DISTINCT sf_account_id
  FROM base_contract_customers_daily
  WHERE is_pof
)

, all_solution_area AS (
  SELECT DISTINCT sf_account_id
  FROM base_contract_customers_daily
  WHERE <!-- SOLUTION_AREA_WHERE_CLAUSE -->
)

, unaggregated_zone as (
   SELECT b.event_date,    
          b.zone_plan,  
          b.zone_id, 
          b.account_id, 
          b.cust_id, 
          b.sf_account_id,
        <!-- GENERATED_COLUMN_SELECTORS -->
      FROM base_zones_daily as b
      LEFT JOIN metrics_daily as e
        ON b.zone_id = e.zone_id
)
<!-- ACCOUNT_METRICS_CTES_PLACEHOLDER -->

<!-- UNAGGREGATED_ALL_PLACEHOLDER -->

, unaggregate_with_type AS ( 
  SELECT b.*, 
          CASE
            WHEN cc.sf_account_id IS NOT NULL THEN 'contract'
            ELSE 'paygo'
          END AS c_customer_type,
  FROM unaggregated_all as b
    LEFT JOIN all_contracted_customers AS cc
      ON b.sf_account_id = cc.sf_account_id
)

-- Covers the following scenarios 
-- all Cloudflare contracted 
-- All Paygo 
-- All active zones 
, all_customers_agg AS (
    SELECT
      <!-- EVENT_DATE_SELECT_CLAUSE -->
      CASE
        WHEN GROUPING(c_customer_type) = 1 THEN 'All'
        ELSE c_customer_type
      END AS scenario,
      CASE WHEN GROUPING(b.zone_plan) = 1 THEN "All"
            ELSE b.zone_plan
        END AS zone_plan,
      -- Denominators: Total distinct counts for each plan
      COUNT(DISTINCT zone_id) AS denominator_zones,
      COUNT(DISTINCT account_id) AS denominator_accts,
      COUNT(DISTINCT cust_id) AS denominator_custs,

      -- Numerators: Define the calculation for each of your metrics here
      <!-- GENERATED_COUNT_STATEMENTS -->
    FROM unaggregate_with_type as b
    GROUP BY GROUPING SETS (
        (c_customer_type, b.zone_plan), -- Set 1: The most detailed level
        (c_customer_type),            -- Set 2: Subtotals for each scenario (creates the 'ALL' plan rows)
        (b.zone_plan),           -- Set 3: Subtotals for each plan (creates the 'overall_total' rows)
        ()                     -- Set 4: The grand total (creates the 'overall_total' / 'ALL' row)
      ) 
  )

-- Covers the following scenarios 
-- all POF
, pof_customers_agg AS (
    SELECT
      <!-- EVENT_DATE_SELECT_CLAUSE -->
      "pof" AS scenario,
      CASE WHEN GROUPING(b.zone_plan) = 1 THEN "All"
            ELSE b.zone_plan
        END AS zone_plan,
      -- Denominators: Total distinct counts for each plan
      COUNT(DISTINCT zone_id) AS denominator_zones,
      COUNT(DISTINCT account_id) AS denominator_accts,
      COUNT(DISTINCT cust_id) AS denominator_custs,

      -- Numerators: Define the calculation for each of your metrics here
      <!-- GENERATED_COUNT_STATEMENTS -->
    FROM unaggregate_with_type as b
    INNER JOIN all_pof as pof 
      ON b.sf_account_id = pof.sf_account_id
    GROUP BY ROLLUP (b.zone_plan)
  )

-- Covers the following scenarios 
-- all solution_area
, solution_area_customers_agg AS (
    SELECT
      <!-- EVENT_DATE_SELECT_CLAUSE -->
      <!-- SOLUTION_AREA_SCENARIO_CLAUSE --> AS scenario,
      CASE WHEN GROUPING(b.zone_plan) = 1 THEN "All"
            ELSE b.zone_plan
        END AS zone_plan,
      -- Denominators: Total distinct counts for each plan
      COUNT(DISTINCT zone_id) AS denominator_zones,
      COUNT(DISTINCT account_id) AS denominator_accts,
      COUNT(DISTINCT cust_id) AS denominator_custs,

      -- Numerators: Define the calculation for each of your metrics here
      <!-- GENERATED_COUNT_STATEMENTS -->
    FROM unaggregate_with_type as b
    INNER JOIN all_solution_area as aps 
      ON b.sf_account_id = aps.sf_account_id 
    GROUP BY ROLLUP (b.zone_plan)
  )

, usage_contracted_customers AS (
   SELECT event_date,
          zone_plan, 
          zone_id, 
          account_id, 
          cust_id, 
          sf_account_id,
          <!-- GENERATED_ADOPTED_ALIASES -->
    FROM unaggregate_with_type as b
    WHERE c_customer_type = 'contract'
)

,unpivoted_contract_usage AS (
    SELECT
          event_date, 
          zone_plan,
          zone_id, 
          account_id, 
          cust_id, 
          sf_account_id,
          adopted_feature,
          is_adopted
    FROM usage_contracted_customers
    UNPIVOT INCLUDE NULLS (
      is_adopted FOR adopted_feature IN (
          <!-- GENERATED_UNPIVOT_FEATURES -->
      )
    )
)

, all_features_contracted_customers AS (
    SELECT * 
     FROM base_contract_customers_daily
    WHERE feature IN (
        <!-- GENERATED_UNPIVOT_FEATURES_QUOTED -->
    )
)



, combined_eligibility AS (
    -- Eligibility for standalone features
    SELECT
        ife.sf_account_id,
        ife.feature AS adopted_feature,
        ife.is_eligible,
        ife.is_contracted
    FROM all_features_contracted_customers AS ife
)

, contract_usage AS (
    SELECT
        u.event_date,
        u.zone_plan,
        u.zone_id,
        u.account_id,
        u.cust_id,
        u.sf_account_id,
        u.adopted_feature as feature,
        u.is_adopted,
        ce.is_eligible,
        ce.is_contracted
    FROM unpivoted_contract_usage AS u
    LEFT JOIN combined_eligibility AS ce
        ON u.sf_account_id = ce.sf_account_id
        AND u.adopted_feature = ce.adopted_feature
)

,scenarios_temp AS (
  SELECT <!-- EVENT_DATE_SELECT_CLAUSE_MAX --> as event_date,
        feature,
        COUNT(DISTINCT CASE WHEN is_adopted AND is_contracted AND is_eligible THEN zone_id END) AS n_eligible_contracted_zones,
        COUNT(DISTINCT CASE WHEN is_adopted AND is_contracted AND is_eligible THEN account_id END) AS n_eligible_contracted_accts,
        COUNT(DISTINCT CASE WHEN is_adopted AND is_contracted AND is_eligible THEN cust_id END) AS n_eligible_contracted_custs,
        COUNT(DISTINCT CASE WHEN is_contracted AND is_eligible THEN zone_id END) AS d_eligible_contracted_zones,
        COUNT(DISTINCT CASE WHEN is_contracted AND is_eligible THEN account_id END) AS d_eligible_contracted_accts,
        COUNT(DISTINCT CASE WHEN is_contracted AND is_eligible THEN cust_id END) AS d_eligible_contracted_custs
    FROM contract_usage 
    WHERE feature IS NOT NULL
    GROUP BY (feature)
)

, eligible_contracted_customers_unpivoted AS ( 
    SELECT 
        DATE(event_date) as event_date,
        'eligible_product_contracted' AS scenario,
        "All" as zone_plan,
        feature,
        n_eligible_contracted_zones as numerator_zones,
        n_eligible_contracted_accts as numerator_accts,
        n_eligible_contracted_custs as numerator_custs,
        d_eligible_contracted_zones as denominator_zones,
        d_eligible_contracted_accts as denominator_accts,
        d_eligible_contracted_custs as denominator_custs
    FROM scenarios_temp 
) 

, all_customers_unpivoted AS (
  SELECT
      DATE(event_date) as event_date,
      agg.scenario,
      agg.zone_plan,
      metrics.feature,
      metrics.numerator_zones,
      metrics.numerator_accts,
      metrics.numerator_custs,
      agg.denominator_zones,
      agg.denominator_accts,
      agg.denominator_custs
    FROM
      all_customers_agg AS agg,
      UNNEST([
          <!-- GENERATED_UNNEST_STRUCTS -->
      ]) 
      AS metrics
)
, pof_customers_unpivoted AS (
  SELECT
      DATE(event_date) as event_date,
      agg.scenario,
      agg.zone_plan,
      metrics.feature,
      metrics.numerator_zones,
      metrics.numerator_accts,
      metrics.numerator_custs,
      agg.denominator_zones,
      agg.denominator_accts,
      agg.denominator_custs
    FROM
      pof_customers_agg AS agg,
      UNNEST([
          <!-- GENERATED_UNNEST_STRUCTS -->
      ])
      AS metrics
)

, solution_area_customers_unpivoted AS (
  SELECT
      DATE(event_date) as event_date,
      agg.scenario,
      agg.zone_plan,
      metrics.feature,
      metrics.numerator_zones,
      metrics.numerator_accts,
      metrics.numerator_custs,
      agg.denominator_zones,
      agg.denominator_accts,
      agg.denominator_custs
    FROM
      solution_area_customers_agg AS agg,
      UNNEST([
          <!-- GENERATED_UNNEST_STRUCTS -->
      ])
      AS metrics
)


SELECT * FROM all_customers_unpivoted
UNION ALL 
SELECT * FROM pof_customers_unpivoted
UNION ALL 
SELECT * FROM solution_area_customers_unpivoted
UNION ALL 
SELECT * FROM eligible_contracted_customers_unpivoted
`;

// SQL CTEs for account-level metrics, conditionally included
const ACCOUNT_METRICS_CTES = `
, base_acct as (
  SELECT DISTINCT event_date,
          'NULL' as zone_plan,
          0 as zone_id,
          account_id, 
          cust_id, 
          sf_account_id, 
   FROM base_zones_daily 
)

, unaggregated_acct as (
    SELECT b.*,
        <!-- GENERATED_COLUMN_SELECTORS -->
      FROM base_acct as b
      LEFT JOIN metrics_daily as e
        ON b.account_id = e.account_id
        AND (COALESCE(e.zone_id, 0) = 0)

)
`;

// Default input pairs for individual and grouped features
const DEFAULT_INPUT_PAIRS = `uses_leaked_credentials_check,leaked_credential_check
uses_cf_waf_content_scan,malware_scan
uses_leaked_credentials_check | uses_account_takeover | uses_disposable_email | uses_sequence_signals,fraud_and_account_abuse`;


// Main App component
const App = () => {
    const [inputText, setInputText] = useState(DEFAULT_INPUT_PAIRS);
    const [generatedCountSql, setGeneratedCountSql] = useState('');
    const [generatedUnnestSql, setGeneratedUnnestSql] = useState('');
    const [fullGeneratedQuery, setFullGeneratedQuery] = useState('');
    const [message, setMessage] = useState('');
    const [metricsTableId, setMetricsTableId] = useState('`cloudflare-bi.bi_public.adoption_rules_metrics_daily`');
    const [includeAccountMetrics, setIncludeAccountMetrics] = useState(true);
    const [excludeInternalZones, setExcludeInternalZonesMetrics] = useState(true);
    const [useEventDateParameter, setUseEventDateParameter] = useState(false);
    const [selectedSolutionArea, setSolutionArea] = useState('is_app_sec');

    const showMessage = (msg) => {
        setMessage(msg);
        setTimeout(() => setMessage(''), 3000);
    };

    const handleInputChange = (e) => {
        setInputText(e.target.value);
    };

    const handleMetricsTableIdChange = (e) => {
        setMetricsTableId(e.target.value);
    };

    const handleIncludeAccountMetricsToggle = (e) => {
        setIncludeAccountMetrics(e.target.checked);
    };

    const handleExcludeInternalZonesToggle = (e) => {
        setExcludeInternalZonesMetrics(e.target.checked);
    };

    const handleUseEventDateParameterToggle = (e) => {
        setUseEventDateParameter(e.target.checked);
    };

    const handleSolutionAreaChange = (event) => {
        setSolutionArea(event.target.value);
    };

    const generateSql = () => {
        const lines = inputText.trim().split('\n');
        let countSnippets = [];
        let unnestStructs = [];
        let adoptedAliases = [];
        let unpivotFeatures = [];
        const allIndividualColumnsSelected = new Set();

        lines.forEach(line => {
            const parts = line.trim().split(',');
            if (parts.length === 2) {
                const rawColumns = parts[0].trim();
                const feature = parts[1].trim();

                const individualColumns = rawColumns.split('|').map(col => col.trim());
                const condition = individualColumns.join(' OR ');

                individualColumns.forEach(col => allIndividualColumnsSelected.add(col));

                countSnippets.push(
                    `      COUNT(DISTINCT CASE WHEN (${condition}) THEN zone_id END) AS ${feature}_zones,`
                );
                countSnippets.push(
                    `      COUNT(DISTINCT CASE WHEN (${condition}) THEN account_id END) AS ${feature}_accts,`
                );
                countSnippets.push(
                    `      COUNT(DISTINCT CASE WHEN (${condition}) THEN cust_id END) AS ${feature}_custs,`
                );

                unnestStructs.push(
                    `STRUCT('${feature}' AS feature, ${feature}_zones AS numerator_zones, ${feature}_accts AS numerator_accts, ${feature}_custs AS numerator_custs)`
                );

                adoptedAliases.push(
                    `          (${condition}) AS ${feature},`
                );

                unpivotFeatures.push(feature);

            } else if (line.trim() !== '') {
                console.warn(`Skipping malformed line: "${line}". Expected "column,feature" or "col1|col2,feature".`);
            }
        });

        // Generate the quoted string for unpivotFeatures
        const unpivotFeaturesQuoted = unpivotFeatures.map(feature => `'${feature}'`).join(', ');


        // Remove trailing comma from the last count snippet
        if (countSnippets.length > 0) {
            countSnippets[countSnippets.length - 1] = countSnippets[countSnippets.length - 1].slice(0, -1);
        }
        // Remove trailing comma from the last adopted alias
        if (adoptedAliases.length > 0) {
            adoptedAliases[adoptedAliases.length - 1] = adoptedAliases[adoptedAliases.length - 1].slice(0, -1);
        }

        setGeneratedCountSql(countSnippets.join('\n'));
        setGeneratedUnnestSql(unnestStructs.join(',\n          '));

        let finalQuery = SQL_TEMPLATE;

        const eventDateWhere = useEventDateParameter ? "'{event_date}'" : "'2025-07-02'";
        const eventDateSelect = useEventDateParameter ? "'{event_date}' AS event_date," : "'2025-07-02' as event_date,";
        const eventDateSelectMax = useEventDateParameter ? "'{event_date}'" : "'2025-07-02'";


        finalQuery = finalQuery.replace(/<!-- METRICS_TABLE_PLACEHOLDER -->/g, metricsTableId);
        finalQuery = finalQuery.replace(/<!-- EVENT_DATE_WHERE_CLAUSE -->/g, eventDateWhere);
        finalQuery = finalQuery.replace(/<!-- EVENT_DATE_SELECT_CLAUSE -->/g, eventDateSelect);
        finalQuery = finalQuery.replace(/<!-- EVENT_DATE_SELECT_CLAUSE_MAX -->/g, eventDateSelectMax);


        const zonesWhereClause = excludeInternalZones ? "zone_plan IS NOT NULL" : "zone_type = 'I'";
        finalQuery = finalQuery.replace(/<!-- EXCLUDE_INTERNAL_ZONES_WHERE_CLAUSE -->/g, zonesWhereClause);

        finalQuery = finalQuery.replace(/<!-- SOLUTION_AREA_WHERE_CLAUSE -->/g, selectedSolutionArea);
        const scenarioSolutionArea = selectedSolutionArea === "is_app_sec" ? "'app_sec_contracted_customer'" : "'app_perf_contracted_customer'";
        finalQuery = finalQuery.replace(/<!-- SOLUTION_AREA_SCENARIO_CLAUSE -->/g, scenarioSolutionArea);

        if (includeAccountMetrics) {
            finalQuery = finalQuery.replace(/<!-- ACCOUNT_METRICS_CTES_PLACEHOLDER -->/g, ACCOUNT_METRICS_CTES);
            finalQuery = finalQuery.replace(/<!-- UNAGGREGATED_ALL_PLACEHOLDER -->/g, `
, unaggregated_all AS (
  SELECT * FROM unaggregated_zone
  UNION ALL
  SELECT * FROM unaggregated_acct
)`);
        } else {
            finalQuery = finalQuery.replace(/<!-- ACCOUNT_METRICS_CTES_PLACEHOLDER -->/g, '');
            finalQuery = finalQuery.replace(/<!-- UNAGGREGATED_ALL_PLACEHOLDER -->/g, `
, unaggregated_all AS (
  SELECT * FROM unaggregated_zone
)`);
        }

        finalQuery = finalQuery.replace(/<!-- GENERATED_COUNT_STATEMENTS -->/g, countSnippets.join('\n'));
        finalQuery = finalQuery.replace(/<!-- GENERATED_UNNEST_STRUCTS -->/g, unnestStructs.join(',\n          '));
        finalQuery = finalQuery.replace(/<!-- GENERATED_ADOPTED_ALIASES -->/g, adoptedAliases.join('\n'));
        finalQuery = finalQuery.replace(/<!-- GENERATED_UNPIVOT_FEATURES -->/g, unpivotFeatures.join(',\n          '));
        finalQuery = finalQuery.replace(/<!-- GENERATED_UNPIVOT_FEATURES_QUOTED -->/g, unpivotFeaturesQuoted);

        // Removed GROUPED_FEATURE_DEFINITIONS_PLACEHOLDER as it's no longer needed
        finalQuery = finalQuery.replace(/<!-- GROUPED_FEATURE_DEFINITIONS_PLACEHOLDER -->/g, '');

        // The GENERATED_CONTRACT_USAGE_FEATURES and eligibility CASE statements are no longer needed
        // as they are replaced by the new combined_eligibility CTE logic.
        finalQuery = finalQuery.replace(/<!-- GENERATED_CONTRACT_USAGE_FEATURES -->/g, '');
        finalQuery = finalQuery.replace(/<!-- GENERATED_ELIGIBILITY_CASE_FORWARD_LOOKING -->/g, '');
        finalQuery = finalQuery.replace(/<!-- GENERATED_ELIGIBILITY_CASE_ELIGIBLE -->/g, '');

        const columnSelectorsString = Array.from(allIndividualColumnsSelected).join(', ');
        const partsToReplaceColumnSelectors = finalQuery.split('<!-- GENERATED_COLUMN_SELECTORS -->');
        if (partsToReplaceColumnSelectors.length > 1) {
            finalQuery = partsToReplaceColumnSelectors[0] + columnSelectorsString + partsToReplaceColumnSelectors[1];
            if (partsToReplaceColumnSelectors.length > 2) {
                finalQuery = finalQuery + columnSelectorsString + partsToReplaceColumnSelectors[2];
            }
        }

        setFullGeneratedQuery(finalQuery);
    };

    const copyToClipboard = (textToCopy) => {
        if (textToCopy) {
            const textarea = document.createElement('textarea');
            textarea.value = textToCopy;
            document.body.appendChild(textarea);
            textarea.select();
            try {
                document.execCommand('copy');
                showMessage('SQL copied to clipboard!');
            } catch (err) {
                console.error('Failed to copy text: ', err);
                showMessage('Failed to copy SQL. Please copy manually.');
            }
            document.body.removeChild(textarea);
        }
    };

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', fontFamily: 'sans-serif' }}>
            <div style={{ backgroundColor: '#ffffff', padding: '2rem', borderRadius: '0.5rem', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', width: '100%', maxWidth: '64rem' }}>
                <h1 style={{ fontSize: '1.875rem', fontWeight: '700', textAlign: 'center', color: '#1f2937', marginBottom: '1.5rem' }}>
                    SQL Syntax Automator
                </h1>

                <p style={{ color: '#4b5563', marginBottom: '1.5rem', textAlign: 'left' }}>
                    Enter your column and feature pairs & adoption view table address below.  The tool will generate a complete SQL query.
                </p>

                {/* Input Area for Column, Feature Pairs */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <label htmlFor="inputPairs" style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                        Column, Feature Pairs  <br />
                        * One feature per line, separated by a comma <br />
                        * Single features: (e.g: `uses_feature_XYZ, feature_XYZ`). <br />
                        * For grouped features, use a pipe `|` to separate columns (e.g: `uses_feature_XYZ | uses_feature_ABC , grouped_feature`). <br />
                    </label>
                    <textarea
                        id="inputPairs"
                        style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontFamily: 'monospace', fontSize: '0.875rem' }}
                        rows="5"
                        placeholder="Example:
uses_XYZ, XYZ
uses_ABC, ABC
uses_bot_management, bot_management
uses_malware_scan|uses_web_attack_score|uses_web_attack_signatures|uses_rate_limiting, web_app_exploits"
                        value={inputText}
                        onChange={handleInputChange}
                    ></textarea>
                </div>

                {/* Input for Metrics Table ID */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <label htmlFor="metricsTableId" style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                        Metrics View ID: <br />
                        * This view should be in this format `bi_public.adoption_XXXX_metrics_daily` & use_feature_XYZ boolean columns
                    </label>
                    <input
                        type="text"
                        id="metricsTableId"
                        style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontFamily: 'monospace', fontSize: '0.875rem' }}
                        value={metricsTableId}
                        onChange={handleMetricsTableIdChange}
                        placeholder="Example `cloudflare-bi.bi_public.adoption_rules_metrics_daily`"
                    />
                </div>

                {/* Radio buttons for solution area */}
                <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center' }}>
                    {/* App Security Option */}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <input
                            type="radio"
                            id="is_app_sec"
                            name="app_option"
                            value="is_app_sec"
                            checked={selectedSolutionArea === 'is_app_sec'}
                            onChange={handleSolutionAreaChange}
                        />
                        <label htmlFor="is_app_sec" style={{ cursor: 'pointer' }}>
                            App Security
                        </label>
                    </div>

                    {/* App Performance Option */}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <input
                            type="radio"
                            id="is_app_perf"
                            name="app_option"
                            value="is_app_perf"
                            checked={selectedSolutionArea === 'is_app_perf'}
                            onChange={handleSolutionAreaChange}
                        />
                        <label htmlFor="is_app_perf" style={{ cursor: 'pointer' }}>
                            App Performance
                        </label>
                    </div>

                </div>


                {/* Toggle for Account Level Metrics */}
                <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                    <input
                        type="checkbox"
                        id="includeAccountMetrics"
                        style={{ marginRight: '0.5rem', height: '1.25rem', width: '1.25rem', accentColor: '#2563eb', borderRadius: '0.25rem', border: '1px solid #d1d5db' }}
                        checked={includeAccountMetrics}
                        onChange={handleIncludeAccountMetricsToggle}
                    />
                    <label htmlFor="includeAccountMetrics" style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '600' }}>
                        Include Account Level Metrics
                    </label>
                </div>

                {/* Toggle for Event Date Parameter */}
                <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center' }}>
                    <input
                        type="checkbox"
                        id="useEventDateParameter"
                        style={{ marginRight: '0.5rem', height: '1.25rem', width: '1.25rem', accentColor: '#2563eb', borderRadius: '0.25rem', border: '1px solid #d1d5db' }}
                        checked={useEventDateParameter}
                        onChange={handleUseEventDateParameterToggle}
                    />
                    <label htmlFor="useEventDateParameter" style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '600' }}>
                        Use Event Date Parameter (`{'{event_date}'}`)
                    </label>
                </div>

                {/* Toggle for Zone Plan Parameter */}
                <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                    <input
                        type="checkbox"
                        id="excludeInternalZones"
                        style={{ marginRight: '0.5rem', height: '1.25rem', width: '1.25rem', accentColor: '#2563eb', borderRadius: '0.25rem', border: '1px solid #d1d5db' }}
                        checked={excludeInternalZones}
                        onChange={handleExcludeInternalZonesToggle}
                    />
                    <label htmlFor="excludeInternalZones" style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '600' }}
                    >
                        Exclude Internal Zones (via zone_plan)
                    </label>
                </div>


                {/* Buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', marginBottom: '2rem', gap: '1rem' }}>
                    <button
                        onClick={generateSql}
                        style={{ backgroundColor: '#2563eb', color: '#ffffff', fontWeight: '700', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', border: 'none', cursor: 'pointer' }}
                    >
                        Generate SQL
                    </button>
                </div>


                {/* Output Area for Full Query */}
                {fullGeneratedQuery && (
                    <div style={{ backgroundColor: '#f9fafb', padding: '1.5rem', borderRadius: '0.5rem', border: '1px solid #e5e7eb', marginBottom: '1.5rem' }}>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>Full Generated SQL Query:</h2>
                        <button
                            onClick={() => copyToClipboard(fullGeneratedQuery)}
                            style={{ backgroundColor: '#16a34a', color: '#ffffff', fontWeight: '700', padding: '0.5rem 1rem', borderRadius: '0.5rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', border: 'none', cursor: 'pointer' }}
                        >
                            Copy Full Query
                        </button>
                        <pre style={{ backgroundColor: '#1f2937', color: '#4ade80', padding: '1rem', borderRadius: '0.375rem', overflowX: 'auto', fontSize: '0.875rem', marginBottom: '1rem' }}>
                            <code>{fullGeneratedQuery}</code>
                        </pre>
                        <button
                            onClick={() => copyToClipboard(fullGeneratedQuery)}
                            style={{ backgroundColor: '#16a34a', color: '#ffffff', fontWeight: '700', padding: '0.5rem 1rem', borderRadius: '0.5rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', border: 'none', cursor: 'pointer' }}
                        >
                            Copy Full Query
                        </button>
                    </div>
                )}

                {/* Output Area for COUNT DISTINCT */}
                {generatedCountSql && (
                    <div style={{ backgroundColor: '#f9fafb', padding: '1.5rem', borderRadius: '0.5rem', border: '1px solid #e5e7eb', marginBottom: '1.5rem' }}>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>Generated COUNT DISTINCT Statements:</h2>
                        <pre style={{ backgroundColor: '#1f2937', color: '#4ade80', padding: '1rem', borderRadius: '0.375rem', overflowX: 'auto', fontSize: '0.875rem', marginBottom: '1rem' }}>
                            <code>{generatedCountSql}</code>
                        </pre>
                        <button
                            onClick={() => copyToClipboard(generatedCountSql)}
                            style={{ backgroundColor: '#16a34a', color: '#ffffff', fontWeight: '700', padding: '0.5rem 1rem', borderRadius: '0.5rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', border: 'none', cursor: 'pointer' }}
                        >
                            Copy COUNT DISTINCT
                        </button>
                    </div>
                )}

                {/* Output Area for UNNEST Block */}
                {generatedUnnestSql && (
                    <div style={{ backgroundColor: '#f9fafb', padding: '1.5rem', borderRadius: '0.5rem', border: '1px solid #e5e7eb', marginBottom: '1.5rem' }}>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>Generated UNNEST Block:</h2>
                        <pre style={{ backgroundColor: '#1f2937', color: '#4ade80', padding: '1rem', borderRadius: '0.375rem', overflowX: 'auto', fontSize: '0.875rem', marginBottom: '1rem' }}>
                            <code>{`UNNEST([\n          ${generatedUnnestSql}\n      ])`}</code>
                        </pre>
                        <button
                            onClick={() => copyToClipboard(`UNNEST([\n          ${generatedUnnestSql}\n      ])`)}
                            style={{ backgroundColor: '#16a34a', color: '#ffffff', fontWeight: '700', padding: '0.5rem 1rem', borderRadius: '0.5rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', border: 'none', cursor: 'pointer' }}
                        >
                            Copy UNNEST Block
                        </button>
                    </div>
                )}

                {/* Message Box */}
                {message && (
                    <div style={{ position: 'fixed', bottom: '1rem', right: '1rem', backgroundColor: '#1f2937', color: '#ffffff', padding: '0.5rem 1rem', borderRadius: '0.375rem', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', zIndex: '50' }}>
                        {message}
                    </div>
                )}
            </div>
        </div>
    );
};

export default App;
